import { Power, PowerOff, Edit } from 'lucide-react';
import { IconDotsVertical } from '@tabler/icons-react';
import { toast } from 'sonner';
import { type AdminUser } from '@/types/user';
import { useAuth } from '@/hooks/useAuth';
import { useUpdateAdminUserStatus } from '../../hooks/useUserManagement';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface UserActionsProps {
  user: AdminUser;
  onEdit?: (user: AdminUser) => void;
  onDelete?: (user: AdminUser) => void;
  onToggleStatus?: (user: AdminUser) => Promise<void>;
}

export function UserActions({
  user,
  onEdit,
  onDelete,
  onToggleStatus,
}: UserActionsProps) {
  const { hasPermission } = useAuth();
  const updateUserStatusMutation = useUpdateAdminUserStatus();

  const handleEdit = () => {
    if (onEdit) {
      onEdit(user);
    } else {
      // TODO: Implement edit when API endpoint is available
      console.log('Edit user:', user);
      toast.info(
        `Edit functionality for ${user.full_name || 'this user'} coming soon!`
      );
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(user);
    } else {
      // TODO: Implement delete when API endpoint is available
      toast.info(
        `Delete functionality for ${user.full_name || 'this user'} coming soon!`
      );
    }
  };

  const handleToggleStatus = async () => {
    if (onToggleStatus) {
      await onToggleStatus(user);
    } else {
      const newStatus = !user.is_active;
      const action = newStatus ? 'activate' : 'deactivate';

      if (
        confirm(
          `Are you sure you want to ${action} ${user.full_name || 'this user'}?`
        )
      ) {
        try {
          await updateUserStatusMutation.mutateAsync({
            user_id: user.id,
            is_active: newStatus,
          });
        } catch {
          // Error is handled by the mutation hook
        }
      }
    }
  };

  // Check permissions
  const canUpdate = hasPermission('user_management:update');
  const canDelete = hasPermission('user_management:delete');

  // Only show actions the user has permission for
  const showEdit = canUpdate && onEdit;
  const showToggleStatus = false; //canUpdate;
  const showDelete = canDelete && onDelete;

  // Don't render anything if user has no permissions
  if (!canUpdate && !canDelete) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="data-[state=open]:bg-muted text-muted-foreground flex size-8 ml-auto"
          size="icon"
          data-testid={`user-actions-${user.id}`}
        >
          <IconDotsVertical />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-32">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {showEdit && (
          <DropdownMenuItem
            onClick={handleEdit}
            data-testid={`user-edit-${user.id}`}
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
        )}
        {showToggleStatus && (
          <DropdownMenuItem
            onClick={handleToggleStatus}
            data-testid={`user-toggle-status-${user.id}`}
          >
            {user.is_active ? (
              <>
                <PowerOff className="mr-2 h-4 w-4" />
                Deactivate
              </>
            ) : (
              <>
                <Power className="mr-2 h-4 w-4" />
                Activate
              </>
            )}
          </DropdownMenuItem>
        )}
        {showDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onClick={handleDelete}
              data-testid={`user-delete-${user.id}`}
            >
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
